import {cn} from '@/matic/lib/utils/cn';
import {
  formatDuration,
  OPEN_STATUSES,
  STATUS_LABEL,
  timeTone,
  type QuoteStatus,
} from './quote-status';

type Props = {
  status: QuoteStatus;
  /** Time since receipt; null when the queue gave none. */
  elapsedMs: number | null;
  /** `sidebar` and `ribbon` sit on the dark shell; `table` sits on white. */
  variant?: 'sidebar' | 'ribbon' | 'table';
  className?: string;
};

/**
 * The desk's time box: the waiting time (or, once closed, done/declined), sized to its text.
 * The colour is the time (slate under a day, orange from a day on, green once closed), so one
 * glance across the sidebar says what is late; the state is in the tooltip.
 */
export function StatusTimeBox({
  status,
  elapsedMs,
  variant = 'sidebar',
  className,
}: Props) {
  const tone = timeTone(status, elapsedMs);
  const onDark = variant !== 'table';
  const closed = !OPEN_STATUSES.has(status);
  const title = closed
    ? STATUS_LABEL[status]
    : elapsedMs === null
      ? STATUS_LABEL[status]
      : `${STATUS_LABEL[status]} · waiting ${formatDuration(elapsedMs)}`;
  return (
    <span
      title={title}
      className={cn(
        'inline-flex h-[22px] shrink-0 items-center rounded px-1.5 font-mono text-[11px] font-semibold tabular-nums',
        tone === 'fresh' && 'bg-[#5b7a99] text-white',
        tone === 'late' && 'bg-primary-600 text-white',
        tone === 'done' &&
          (onDark ? 'text-success-400' : 'bg-success-100 text-success-700'),
        className
      )}
    >
      {closed ? status : elapsedMs === null ? '—' : formatDuration(elapsedMs)}
    </span>
  );
}
